/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import classname from 'classnames';
import get from 'lodash/get';
import { computed } from 'mobx';
import * as React from 'react';

import {
    getGroups,
    getTenderIdOperationIdMap,
    IGetOrderSummaryInput,
    IGroups,
    IHelp,
    IOrderSummary,
    IPaymentMethods,
    IPaymentMethodsInput,
    IPriceContext,
    ISalesLine,
} from '@msdyn365-commerce-modules/order-management';

import { getOrderInformationInput, getOrderInformation, IOrderInformation } from './components/get-order-information';

import { Button, Heading, IModuleProps, INodeProps } from '@msdyn365-commerce-modules/utilities';
import { getUrlSync } from '@msdyn365-commerce/core';
import { CartLine, SalesOrder, SimpleProduct, OrderOriginator } from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';

import { publish } from '../../Utilities/analytics/analytics-dispatcher';
import { getAttributesForCart, IAttributeBank } from '../../Utilities/get-product-attributes';

import { wineClubSetup } from '../../settings/app.settings';
import { WineClubDataRef, WineClubDataStore } from '../wine-club-signup/wine-club-data';
import { getOrderDetailsHelp } from './components/get-help';
import { getOrderDetailsOrderSummary } from './components/get-order-summary';
import { getOrderDetailsPaymentMethods } from './components/get-payment-methods';
import { getSalesLines, getSalesLinesInput } from './components/get-sales-lines';
import { ISmweOrderDetailsData } from './smwe-order-details.data';
import { ISmweOrderDetailsProps } from './smwe-order-details.props.autogenerated';

export * from './components/get-help';
export * from './components/get-order-summary';
export * from './components/get-payment-methods';

export interface IPaymentMethodsInputExtended extends IPaymentMethodsInput {
    googlePayLabel: string;
    applePayLabel: string;
}
export interface IOrderDetailsViewProps extends ISmweOrderDetailsProps<ISmweOrderDetailsData> {
    moduleProps: IModuleProps;
    salesOrder?: SalesOrder;
    footerProps?: INodeProps;
    alert?: React.ReactNode;
    loading?: React.ReactNode;
    heading: React.ReactNode;
    backToShoppingLink?: React.ReactNode;
    orderInfomation?: IOrderInformation;
    salesLines?: ISalesLine[];
    groups?: IGroups;
    orderSummary?: IOrderSummary;
    payment?: IPaymentMethods;
    help?: IHelp;
    isWineClubOrder?: boolean;
    isWineClubPickup?: boolean;
    wineClubData?: WineClubDataRef;
}

/**
 *
 * OrderDetails component
 * @extends {React.Component<ISmweOrderDetailsProps<ISmweOrderDetailsData>>}
 */
class OrderDetails extends React.Component<ISmweOrderDetailsProps<ISmweOrderDetailsData>> {
    private moduleName: string = 'ms-order-details';
    private parsedProductAttributes: IAttributeBank = {};
    private wineClubData: WineClubDataRef;

    @computed get isCurrentChannel(): boolean {
        if (!this.order) {
            return false;
        }
        return this.order.ChannelId === get(this.props, 'context.request.apiSettings.channelId');
    }

    @computed get isDataReady(): boolean {
        return get(this.props, 'data.orderHydration.status') === 'SUCCESS';
    }

    @computed get hasError(): boolean {
        return get(this.props, 'data.orderHydration.status') === 'FAILED';
    }

    @computed get order(): SalesOrder | undefined {
        return get(this.props, 'data.orderHydration.result.salesOrder');
    }

    @computed get products(): SimpleProduct[] {
        return get(this.props, 'data.orderHydration.result.products') || [];
    }

    @computed get priceContext(): IPriceContext {
        const {
            id,
            typeName,
            context,
            telemetry,
            resources: { freePriceText }
        } = this.props;
        return {
            id,
            typeName,
            context,
            telemetry,
            freePriceText
        };
    }

    @computed get earnedPoints(): number {
        const loyaltyCard = this.props.data.loyaltyCard.result;
        if (loyaltyCard && loyaltyCard.RewardPoints && this.order) {
            const pointPrograms = loyaltyCard.RewardPoints.map(point => point.RewardPointId);
            const rewardLines = this.order.LoyaltyRewardPointLines || [];
            let total = 0;
            rewardLines.forEach(line => {
                if ((line.RewardPointAmountQuantity || 0) > 0 && pointPrograms.indexOf(line.RewardPointId) > -1) {
                    total += line.RewardPointAmountQuantity!;
                }
            });
            return total;
        }
        return 0;
    }

    @computed get orderSummaryProps(): IGetOrderSummaryInput | undefined {
        if (!this.order) {
            return;
        }

        const {
            resources: { orderSummaryHeading, orderSummaryItemsTotalLabel, orderSummaryShippingFeeLabel, orderSummaryTaxLabel, orderSummaryGrandTotalLabel, pointsEarnedLabel }
        } = this.props;
        return {
            order: this.order,
            earnedPoints: this.earnedPoints,
            priceContext: this.priceContext,
            resource: {
                orderSummaryHeading,
                orderSummaryItemsTotalLabel,
                orderSummaryShippingFeeLabel,
                orderSummaryTaxLabel,
                pointsEarnedLabel,
                orderSummaryGrandTotalLabel
            }
        };
    }

    @computed get paymentMethodsProps(): IPaymentMethodsInputExtended | undefined {
        const tenderTypes = this.props.data.tenderTypes.result;
        if (!this.order || !this.order.TenderLines || !tenderTypes) {
            return;
        }

        const locale = get(this.props, 'context.request.locale') || 'en-US';

        const {
            resources: {
                paymentMethodsTitle,
                creditCardEndingLabel,
                giftCardEndingLabel,
                amountCoveredLabel,
                cashUsedLabel,
                loyaltyCardUsedLabel,
                customerAccountUsedLabel,
                googlePayLabel,
                applePayLabel
            }
        } = this.props;

        return {
            priceContext: this.priceContext,
            tenderLines: this.order.TenderLines,
            tenderIdOperationIdMap: getTenderIdOperationIdMap(tenderTypes),
            locale,
            resource: {
                paymentMethodsTitle,
                creditCardEndingLabel,
                giftCardEndingLabel,
                amountCoveredLabel,
                cashUsedLabel,
                loyaltyCardUsedLabel,
                customerAccountUsedLabel,
            },
            googlePayLabel,
            applePayLabel
        };
    }

    // this should be moved to a utility (WineClubUtils) or something
    @computed get wineClubIds(): string[] {
        const wineClub = this.props.app.config.wineClubSetup as wineClubSetup | undefined;
        const ids: string[] = [];
        wineClub && wineClub.forEach(club => club.variants.forEach(variant => ids.push(variant.itemId)));
        return ids;
    }

    @computed get isWineClubOrder(): boolean {
        if (!this.order) { return false; }

        const lines = this.order.SalesLines || [];

        // clubs have only one item
        if (lines.length > 1) { return false; }

        for (const line of lines) {
            const isClubOrder = this.wineClubIds.find(clubId => +clubId === line.ProductId);
            if (isClubOrder) { return true; }
        }

        return false;
    }

    @computed get isWineClubPickup(): boolean {
        return this.wineClubData.deliveryType === 'PICKUP';
    }

    public constructor(props: ISmweOrderDetailsProps<ISmweOrderDetailsData>) {
        super(props);

        this.wineClubData = WineClubDataStore.getInstance().get();
    }

    //----------------------------------------------------------
    //----------------------------------------------------------
    public componentDidMount(): void {
        // Don't do anything unless emitAnalyticsEvent is enabled
        if (!this.props.config.emitAnalyticsEvent) {
            return;
        }

        // Wait for sales order and product data to load
        this.props.data.orderHydration.then(data => {

            // Ensure it loaded successfully
            if (this.isDataReady) {

                // Fetch attributes, then generate the checkout event
                this._setProductAttributes()
                    .then(() => this._generatePurchaseAnalyticsEvent())
                    .catch(e => this.props.telemetry.exception(e));
            }
        }).catch(e => this.props.telemetry.exception(e));
        WineClubDataStore.getInstance().reset();
    }

    //----------------------------------------------------------
    //----------------------------------------------------------
    public render(): JSX.Element {
        const {
            resources: {
                noSalesOrderText,
                genericErrorMessage,
                needHelpLabel,
                helpLineNumberLabel,
                loadingLabel,
                backToShopping,
                orderItemLabel,
                orderItemsLabel,
                helpLineContactAriaLabel,
            },

            config: { heading, contactNumber, showChannelInfo, showChannelName, shouldShowQrCode} } = this.props;
        const customer = this.props.data.customerInformation && this.props.data.customerInformation.result;
        const originators = this.props.data.orderOriginators && this.props.data.orderOriginators.result;
        const originator: OrderOriginator | undefined = originators && originators.length === 1 ? originators[0] : undefined;
        const moduleProps = { moduleProps: this.props, className: classname('ms-order-details', this.props.config.className) };
        const backToShoppingUrl = getUrlSync('home', this.props.context.actionContext) || '';
        const channelIdentities = this.props.data.channels.result && this.props.data.channels.result.channelIdentities;
        const orgUnitLocations = this.props.data.orgUnitLocations.result && this.props.data.orgUnitLocations.result.orgUnitLocations;

        let viewProps: IOrderDetailsViewProps = {
            ...this.props,
            moduleProps,
            heading: heading && <Heading {...heading} className='ms-order-details__heading' />,
            backToShoppingLink: backToShoppingUrl && (
                <Button className='ms-order-details__btn-keep-shopping' href={backToShoppingUrl}>
                    {backToShopping}
                </Button>
            ),
            footerProps: { className: 'ms-order-details__footer' }
        };

        if (this.hasError) {
            viewProps = {
                ...viewProps,
                alert: <span className='ms-order-details__alert'>{genericErrorMessage}</span>
            };
            return this.props.renderView(viewProps) as React.ReactElement;
        }

        if (!this.isDataReady) {
            viewProps = {
                ...viewProps,
                loading: <span className='ms-order-details__loading'>{loadingLabel}</span>
            };
            return this.props.renderView(viewProps) as React.ReactElement;
        }

        if (!this.order) {
            viewProps = {
                ...viewProps,
                alert: <span className='ms-order-details__alert'>{noSalesOrderText}</span>
            };
            return this.props.renderView(viewProps) as React.ReactElement;
        }

        const salesLinesInput = getSalesLinesInput(this.props, this.moduleName, this.order, this.products, this.priceContext);
        const salesLines = salesLinesInput && getSalesLines(salesLinesInput);

        const orderInfomationInput = this.order && getOrderInformationInput(this.props, this.moduleName, this.order, this.priceContext, channelIdentities, orgUnitLocations, customer, originator, showChannelInfo, showChannelName, false, shouldShowQrCode ?? true);
        const orderInfomation = orderInfomationInput && getOrderInformation(orderInfomationInput);

        viewProps = {
            ...viewProps,
            salesOrder: this.order,
            orderInfomation,
            salesLines,
            groups: salesLines
                ? getGroups({
                    moduleName: this.moduleName,
                    salesLines,
                    resources: { orderItemLabel, orderItemsLabel }
                })
                : undefined,
            orderSummary: this.orderSummaryProps ? getOrderDetailsOrderSummary(this.orderSummaryProps) : undefined,
            payment: this.paymentMethodsProps ? getOrderDetailsPaymentMethods(this.paymentMethodsProps) : undefined,
            help: contactNumber ? getOrderDetailsHelp({ needHelpLabel, helpLineNumberLabel, contactNumber, helpLineContactAriaLabel }) : undefined,
            isWineClubOrder: this.isWineClubOrder,
            isWineClubPickup: this.isWineClubPickup,
            wineClubData: this.wineClubData
        };

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    //----------------------------------------------------------
    //----------------------------------------------------------
    private _generatePurchaseAnalyticsEvent(): void {
        if (this.order?.SalesLines) {

            // Format product data for Purchase event
            const products = this.order.SalesLines.map(line => {
                const product = this.products.find(entry => entry.RecordId === line.ProductId);

                // For some reason, if we properly use the external constant for IsSubsciption (or better yet, the isLineSubscriptionLine
                // helper) React explodes with a cryptic error about an undefined component.
                const isSubscription = !!line?.ExtensionProperties?.find(ext => ext.Key === 'IsSubscription');

                return {
                    product,
                    quantity: line.Quantity,
                    subscription: isSubscription,
                    discounts: line.DiscountLines,
                    attributes: this.parsedProductAttributes[line.ProductId!],
                    price: line.NetAmount ? (line.NetAmount / (line.Quantity || 1)).toFixed(2) : line.Price,

                    context: this.props.context,
                };
            });

            // Generate a GA event
            publish('purchase', {
                id: this.order.ChannelReferenceId,  // Id = transaction ID, ChannelReferenceId = Confirmation #
                revenue: this.order.TotalAmount,
                tax: this.order.TaxAmount,
                shipping: this.order.ChargeAmount,
                coupon: this.order.Coupons?.map(coupon => coupon.Code).join(','),
                affiliation: this.order.ChannelId,
                products
            });
        }
    }

    //----------------------------------------------------------
    // Fetch product attributes for all products on a sales order
    //----------------------------------------------------------
    private async _setProductAttributes(): Promise<void> {
        if (this.order) {
            const salesLines = this.order.SalesLines;

            if (salesLines) {
                const fakeCartLines: CartLine[] = salesLines.map(line => ({ ProductId: line.ProductId }));
                this.parsedProductAttributes = await getAttributesForCart(fakeCartLines, this.props.context.actionContext);
            }
        }
    }

}

export default OrderDetails;
